// API utilities for ZemedicAI

// Base API URL, baked in at BUILD time by Create React App — setting it on the
// host after a build has no effect; it has to be present when `yarn build` runs.
//
// Two failure modes this guards against, both of which used to surface as a
// silent 404 against the site's own origin rather than anything diagnosable:
//   1. The variable is unset, so `${API_URL}/api/...` became "undefined/api/..."
//   2. The URL has a trailing slash, so the path became "https://host//api/..."
//
// Falling back to same-origin is correct for the single-container deployment
// (nginx proxies /api to the backend). On a split deployment — React on Vercel,
// FastAPI on HuggingFace — same-origin is wrong, so we say so loudly.
const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

if (!process.env.REACT_APP_BACKEND_URL && typeof window !== 'undefined') {
  // eslint-disable-next-line no-console
  console.error(
    '[ZemedicAI] REACT_APP_BACKEND_URL was not set at build time. API calls ' +
    'will be sent to this site\'s own origin, which only works if a backend ' +
    'is proxied there. For a split deployment, set it in the hosting ' +
    'provider\'s environment variables and REBUILD.'
  );
}

// Helper function for handling API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    // A cached guest_token can outlive the backend that issued it (e.g. after
    // a backend migration) or simply expire — either way the fix is the same:
    // drop it so the next attempt fetches a fresh one via ensureToken(),
    // instead of leaving the visitor permanently stuck on stale credentials.
    if (response.status === 401) {
      localStorage.removeItem('guest_token');
    }
    // Backend may be unreachable/misconfigured — body won't always be JSON
    let detail;
    try {
      const errorData = await response.json();
      detail = errorData.detail;
    } catch {
      detail = `The analysis service is unreachable (HTTP ${response.status}). Please try again shortly.`;
    }
    if (response.status === 401) {
      detail = 'Your demo session expired — please try again.';
    }
    throw new Error(detail || response.statusText);
  }
  return response.json();
};

// Optional patient demographics — age/sex/diabetes history. None of the
// vision models take these as literal model inputs (they're pure image
// classifiers), but they materially affect clinical interpretation (e.g.
// DR risk scales with diabetes duration; melanoma risk rises with age), so
// the backend threads them into Zafya AI's clinician-facing text and stores
// them with the analysis record for future training-data richness.
// camelCase (JS) -> snake_case (FastAPI Form fields). This mapping is explicit
// rather than derived, because a silent casing mismatch here is invisible: the
// request still returns 200 and the context is simply dropped on the floor.
const CLINICAL_TEXT_MAP = {
  symptoms: 'symptoms',
  symptomDuration: 'symptom_duration',
  medicalHistory: 'medical_history',
  medications: 'medications',
  clinicalNotes: 'clinical_notes',
};

const appendDemographics = (formData, demographics) => {
  if (!demographics) return;
  const { age, sex, isKnownDiabetic, diabetesDurationYears } = demographics;
  if (age !== undefined && age !== null && age !== '') formData.append('age', age);
  if (sex) formData.append('sex', sex);
  if (isKnownDiabetic !== undefined && isKnownDiabetic !== null && isKnownDiabetic !== '') {
    formData.append('is_known_diabetic', isKnownDiabetic);
  }
  if (diabetesDurationYears !== undefined && diabetesDurationYears !== null && diabetesDurationYears !== '') {
    formData.append('diabetes_duration_years', diabetesDurationYears);
  }
  // Free-text clinical context (symptoms, history, medications, notes). Lives
  // on the same object as the demographics so every existing analyze* call
  // picks it up without a signature change.
  Object.entries(CLINICAL_TEXT_MAP).forEach(([jsKey, formKey]) => {
    const v = demographics[jsKey];
    if (typeof v === 'string' && v.trim()) formData.append(formKey, v.trim());
  });
};

// Function to upload and analyze chest X-ray images
export const analyzeXray = async (file, token, demographics) => {
  const formData = new FormData();
  formData.append('file', file);
  appendDemographics(formData, demographics);

  const response = await fetch(`${API_URL}/api/analyze/xray`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: formData
  });

  return handleResponse(response);
};

// Function to upload and analyze skin lesion images
export const analyzeSkin = async (file, token, demographics) => {
  const formData = new FormData();
  formData.append('file', file);
  appendDemographics(formData, demographics);

  const response = await fetch(`${API_URL}/api/analyze/skin`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: formData
  });

  return handleResponse(response);
};

// Function to upload and analyze Ultrasound images
export const analyzeCTScan = async (file, token, demographics) => {
  const formData = new FormData();
  formData.append('file', file);
  appendDemographics(formData, demographics);

  const response = await fetch(`${API_URL}/api/analyze/ct-scan`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: formData
  });

  return handleResponse(response);
};

// Obtain a usable bearer token. If the user is logged in, callers pass their
// token. If not (e.g. a walk-up investor demo), lazily create + cache a guest
// session so uploads work without a login detour. Tokens last ~1 week.
export const ensureToken = async (existingToken) => {
  if (existingToken) return existingToken;
  const cached = localStorage.getItem('guest_token');
  if (cached) return cached;
  const rand = Math.random().toString(36).slice(2, 10);
  const res = await fetch(`${API_URL}/api/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: `guest_${rand}@demo.zemedicai.com`,
      password: rand,
      name: 'Guest',
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Could not start a demo session');
  localStorage.setItem('guest_token', data.access_token);
  return data.access_token;
};

// Map the backend analysis response to the shape the result UI expects.
// Backend returns: { model_status, predictions:[{label,confidence(0-1),risk}],
//   top_finding, severity, risk, recommendations:[...], assistant:{clinician,patient},
//   assistant_name, region_of_interest:{x,y,radius}|null, disclaimer }
const SEVERITY_BY_RISK = {
  malignant: 'Severe', 'pre-malignant': 'Moderate',
  benign: 'Mild', normal: 'None', finding: 'Moderate',
  // retina DR grades
  critical: 'Severe', severe: 'Severe', high: 'Severe',
  moderate: 'Moderate', mild: 'Mild', low: 'None',
  // fetal plane — scan-quality tiers, not clinical severity; mapped to the
  // most neutral available badge rather than implying a health finding.
  adequate: 'None', review: 'Mild',
};

export const normalizeAnalysis = (resp) => {
  const preds = resp.predictions || [];
  const id = resp.id;
  // Real region derived from the model's own attention (ViT modalities) or
  // Gemini's bounding estimate (foot) — only the TOP finding gets a location,
  // since that's what the region-of-interest actually corresponds to. When
  // the backend has no localization signal for this modality (e.g. X-ray's
  // CNN backbone), region_of_interest is null and no heatmap dot is drawn
  // rather than fabricating a fake center-of-image location.
  const roi = resp.region_of_interest || null;
  const conditions = preds.map((p, i) => {
    const probability = Math.round((p.confidence || 0) * 100);
    const severity =
      SEVERITY_BY_RISK[p.risk] ||
      (probability >= 70 ? 'Moderate' : probability >= 40 ? 'Mild' : 'None');
    return {
      name: p.label,
      probability,
      severity,
      location: i === 0 ? roi : null,
    };
  });
  const top = conditions[0];
  const assistant = resp.assistant || {};
  return {
    id,
    success: resp.model_status === 'ready',
    modelStatus: resp.model_status,
    conditions,
    confidence: top ? top.probability : 0,
    findings:
      assistant.clinician ||
      (top ? `Leading finding: ${top.name}.` : 'No findings produced.'),
    recommendation: (resp.recommendations || []).join(' '),
    assistant: resp.assistant || null,
    assistantName: resp.assistant_name || 'Zafya AI',
    disclaimer: resp.disclaimer || null,
  };
};

// Function to analyze a breast ultrasound image
export const analyzeBreast = async (file, token, demographics) => {
  const formData = new FormData();
  formData.append('file', file);
  appendDemographics(formData, demographics);

  const response = await fetch(`${API_URL}/api/analyze/breast`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });

  return handleResponse(response);
};

// Retinal fundus — diabetic retinopathy grading
export const analyzeRetina = async (file, token, demographics) => {
  const formData = new FormData();
  formData.append('file', file);
  appendDemographics(formData, demographics);
  const response = await fetch(`${API_URL}/api/analyze/retina`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });
  return handleResponse(response);
};

// Diabetic foot ulcer — Gemini Vision classification
export const analyzeFoot = async (file, token, demographics) => {
  const formData = new FormData();
  formData.append('file', file);
  appendDemographics(formData, demographics);
  const response = await fetch(`${API_URL}/api/analyze/foot`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });
  return handleResponse(response);
};

// Fetal ultrasound frame — plane/view classification (scan-quality check,
// not a diagnostic screen; see ai_models.analyze_fetal_plane_image docstring).
export const analyzeFetalPlane = async (file, token, demographics) => {
  const formData = new FormData();
  formData.append('file', file);
  appendDemographics(formData, demographics);
  const response = await fetch(`${API_URL}/api/analyze/fetal-plane`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });
  return handleResponse(response);
};

// Zafya AI — conversational follow-up assistant. Doctor gets clinical
// register, patient gets plain-language register. `context` carries the
// current analysis (modality, top_finding, severity, risk, age, sex) so
// follow-up answers are grounded in the actual screening result.
export const zafyaChat = async (token, { message, history, audience, context, analysisId, language }) => {
  const response = await fetch(`${API_URL}/api/zafya/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      message,
      history: history || [],
      audience: audience || 'patient',
      context: context || null,
      analysis_id: analysisId || null,
      // Patient-facing render language. English is the default and the only
      // clinician-validated option; the server reports language_downgraded
      // when a requested language could not be served.
      language: language || 'en',
    }),
  });
  return handleResponse(response);
};

// Rate a specific Zafya reply (thumbs up/down + optional comment) — the
// training-signal capture point. Open to any authenticated user on their
// own analysis, not just superadmins.
export const submitZafyaFeedback = async (token, analysisId, { turnId, rating, comment }) => {
  const response = await fetch(`${API_URL}/api/analysis/${analysisId}/zafya-feedback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ turn_id: turnId, rating, comment: comment || null }),
  });
  return handleResponse(response);
};

// Function to get user's analysis history
export const getUserHistory = async (token) => {
  const response = await fetch(`${API_URL}/api/user/history`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  return handleResponse(response);
};

// Function to get a specific analysis result by ID
export const getAnalysisById = async (id, token) => {
  const response = await fetch(`${API_URL}/api/analysis/${id}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  return handleResponse(response);
};

// Function to create a PDF report for an analysis
export const generateReport = async (analysisId, token) => {
  const response = await fetch(`${API_URL}/api/analysis/${analysisId}/report`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || response.statusText);
  }

  return response.blob();
};

// Function to update user profile
export const updateUserProfile = async (userData, token) => {
  const response = await fetch(`${API_URL}/api/user/profile`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(userData)
  });

  return handleResponse(response);
};

// ----------------------------------------
// Superadmin dashboard — every call below 403s server-side unless the
// account has role === 'superadmin'; the frontend route guard is a UX nicety
// only, not the actual enforcement.
// ----------------------------------------
export const adminGetMetrics = async (token) => {
  const response = await fetch(`${API_URL}/api/admin/metrics`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  return handleResponse(response);
};

export const adminListAnalyses = async (token, { modality, reviewStatus, dateFrom, dateTo, page, pageSize } = {}) => {
  const params = new URLSearchParams();
  if (modality) params.set('modality', modality);
  if (reviewStatus) params.set('review_status', reviewStatus);
  if (dateFrom) params.set('date_from', dateFrom);
  if (dateTo) params.set('date_to', dateTo);
  params.set('page', page || 1);
  params.set('page_size', pageSize || 25);
  const response = await fetch(`${API_URL}/api/admin/analyses?${params.toString()}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  return handleResponse(response);
};

export const adminGetAnalysis = async (token, id) => {
  const response = await fetch(`${API_URL}/api/admin/analyses/${id}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  return handleResponse(response);
};

export const adminSubmitZafyaFeedback = async (token, analysisId, { turnId, rating, comment }) => {
  const response = await fetch(`${API_URL}/api/admin/analyses/${analysisId}/zafya-feedback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ turn_id: turnId, rating, comment: comment || null }),
  });
  return handleResponse(response);
};

// ---------------------------------------------------------------------------
// Multi-image encounters
// ---------------------------------------------------------------------------

// The anatomical sites the encounter uploader can tag images with, and the
// model that reads each. Fetched rather than hardcoded so adding a modality
// server-side does not need a matching frontend release.
export const getEncounterSites = async () => {
  const response = await fetch(`${API_URL}/api/encounter/sites`);
  return handleResponse(response);
};

/**
 * Analyse several images from one patient visit as a single encounter.
 *
 * `studies` is an array of { file, site } — e.g. both eyes and both feet from
 * one screening. The `sites` form field must stay parallel to the uploaded
 * files, which is why both are built in the same loop here rather than from
 * two separate passes over state.
 */
export const analyzeEncounter = async (studies, token, context) => {
  const formData = new FormData();
  const sites = [];
  studies.forEach(({ file, site }) => {
    formData.append('files', file);
    sites.push(site);
  });
  formData.append('sites', sites.join(','));
  appendDemographics(formData, context);

  const response = await fetch(`${API_URL}/api/analyze/encounter`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  return handleResponse(response);
};

// Live, deliberately billable check that the hosted LLM providers actually have
// quota — /api/models/status is non-billing by design and cannot tell you this.
// Worth running before a demo.
export const probeModelProviders = async (token) => {
  const response = await fetch(`${API_URL}/api/models/probe`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse(response);
};
